import React, { useContext, useEffect, useState } from "react";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import AuthContext from "../../context/auth/authContext";
import TripContext from "../../context/trip/tripContext";
import SpotReviewContext from "../../context/spot/spotReviewContext";
import TripItem from "../trips/TripItem";
import SpotReviewItemV2 from "../spots/SpotReviewItemV2";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";

const TRIPS_PAGE_SIZE = 3;
const REVIEWS_PAGE_SIZE = 6;

const MyJourneys = () => {
  const authContext = useContext(AuthContext);
  const { loadUser } = authContext;

  const tripContext = useContext(TripContext);
  const {
    userTrips,
    userTripsTotal,
    userTripsPage,
    userTripsHasMore,
    getUserTrips,
    loading: tripsLoading,
  } = tripContext;

  const spotContext = useContext(SpotReviewContext);
  const {
    userReviews,
    userReviewsTotal,
    userReviewsPage,
    userReviewsHasMore,
    getUserReviews,
    loading: reviewsLoading,
  } = spotContext;

  const [tab, setTab] = useState(0);

  useEffect(() => {
    loadUser();
    getUserTrips(1, TRIPS_PAGE_SIZE, false);
    getUserReviews(1, REVIEWS_PAGE_SIZE, false);
    // eslint-disable-next-line
  }, []);

  const loadMoreTrips = () => {
    getUserTrips((userTripsPage || 1) + 1, TRIPS_PAGE_SIZE, true);
  };
  const loadMoreReviews = () => {
    getUserReviews((userReviewsPage || 1) + 1, REVIEWS_PAGE_SIZE, true);
  };

  const tripsSentinelRef = useInfiniteScroll(
    tab === 0 && userTripsHasMore,
    tripsLoading,
    loadMoreTrips
  );
  const reviewsSentinelRef = useInfiniteScroll(
    tab === 1 && userReviewsHasMore,
    reviewsLoading,
    loadMoreReviews
  );

  const tripCount = userTripsTotal || 0;
  const reviewCount = userReviewsTotal || 0;

  return (
    <Box sx={{ width: '100%', pt: 8 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          My Journeys
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          All the trips and reviews you've created in one place.
        </Typography>
      </Box>

      {/* Tabs (stat numbers double as tab switchers) */}
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          mb: 4,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            alignItems: 'flex-start',
            textAlign: 'left',
            py: { xs: 1, sm: 1.5 },
            px: { xs: 1.5, sm: 3 },
            minHeight: 0,
            minWidth: 0,
          },
        }}
      >
        <Tab
          label={
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {tripCount}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {tripCount === 1 ? 'Trip' : 'Trips'}
              </Typography>
            </Box>
          }
        />
        <Tab
          label={
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {reviewCount}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {reviewCount === 1 ? 'Review' : 'Reviews'}
              </Typography>
            </Box>
          }
        />
      </Tabs>

      {/* My Trips tab */}
      {tab === 0 && (
        <Box>
          {tripsLoading && (!userTrips || userTrips.length === 0) ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
          ) : userTrips && userTrips.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {userTrips.map((trip) => (
                  <TripItem key={trip.id} trip={trip} showButton={true} showDelete={true} fromPath="/my-journeys" />
                ))}
              </Box>
              {userTripsHasMore && (
                <Box ref={tripsSentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                py: 5,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                You haven't created any trips yet.
              </Typography>
              <Typography
                component="a"
                href="/create/trip"
                variant="body2"
                sx={{ color: 'primary.main' }}
              >
                Create your first trip
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* My Reviews tab */}
      {tab === 1 && (
        <Box>
          {reviewsLoading && (!userReviews || userReviews.length === 0) ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
          ) : userReviews && userReviews.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: { xs: 2, sm: 3 },
                }}
              >
                {userReviews.map((review) => (
                  <SpotReviewItemV2 key={review.id} spotReview={review} showDelete={true} fromPath="/my-journeys" />
                ))}
              </Box>
              {userReviewsHasMore && (
                <Box ref={reviewsSentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                py: 5,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                You haven't written any reviews yet.
              </Typography>
              <Typography
                component="a"
                href="/create/review"
                variant="body2"
                sx={{ color: 'primary.main' }}
              >
                Write your first review
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MyJourneys;
